import { useState, useEffect, useRef } from 'react';
import { callMfaVerify } from '../../api/mfaApi.js';

/**
 * MfaVerifyStep
 *
 * Second-factor verification shown after tenant-login returns 'mfa-challenge'.
 * Handles both WebAuthn (passkey / biometric) and TOTP (6-digit code).
 *
 * Props:
 *   method         'webauthn' | 'totp' — which factor the user enrolled
 *   challengeToken  Short-lived mfa-challenge JWT from tenant-login
 *   totpTag        string? — short tag naming the live authenticator entry
 *                  ("K7QM"). Absent for users enrolled before tags existed;
 *                  the prompt then keeps its original wording rather than
 *                  naming an entry that is not on their phone.
 *   onSuccess       (token, user) → void — called on successful verification
 *   onBack          () → void — go back to the login form
 *   onRecovery      () → void — start the email recovery flow ("lost your
 *                   authenticator"). Omit to hide the link.
 *   functionsBaseUrl  string? — env-specific Function App base URL (test FA for
 *                     a beta tenant during central login); omit for current env
 *   callVerify     (action, body, baseUrl) → Promise — the verify call, with
 *                  mfa-verify's actions ('webauthn-options' / 'webauthn-verify'
 *                  / 'totp'). Defaults to mfa-verify; super-admin sign-in
 *                  passes its own (superadmin-login-verify, FDNA-02b-2).
 */
function MfaVerifyStep({
  method,
  challengeToken,
  totpTag,
  onSuccess,
  onBack,
  onRecovery,
  functionsBaseUrl,
  callVerify = callMfaVerify,
}) {
  const [totpCode, setTotpCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // WebAuthn: auto-trigger on mount; guard against double-invocation in StrictMode
  const webAuthnStarted = useRef(false);

  useEffect(() => {
    if (method === 'webauthn' && !webAuthnStarted.current) {
      webAuthnStarted.current = true;
      startWebAuthn();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── WebAuthn ─────────────────────────────────────────────────────────────

  async function startWebAuthn() {
    setLoading(true);
    setError('');

    try {
      // 1. Get authentication challenge from backend
      const optionsResult = await callVerify(
        'webauthn-options',
        { challengeToken },
        functionsBaseUrl
      );
      if (optionsResult.error) {
        setError(optionsResult.error);
        setLoading(false);
        return;
      }

      // 2. Trigger browser's WebAuthn UI (biometric / security key prompt)
      const { startAuthentication } = await import('@simplewebauthn/browser');
      const authenticationResponse = await startAuthentication({
        optionsJSON: optionsResult.authenticationOptions,
      });

      // 3. Verify assertion with backend
      const verifyResult = await callVerify(
        'webauthn-verify',
        {
          authToken: optionsResult.authToken,
          authenticationResponse,
          origin: window.location.origin,
        },
        functionsBaseUrl
      );

      if (verifyResult.error) {
        setError(verifyResult.error);
        setLoading(false);
        return;
      }

      onSuccess(verifyResult.token, verifyResult.user);
    } catch (e) {
      if (e?.name === 'NotAllowedError') {
        setError('Authentication was cancelled or timed out. Click "Try again" to retry.');
      } else if (e?.name === 'InvalidStateError') {
        setError('No passkey registered on this device. Contact your administrator.');
      } else if (e?.name === 'SecurityError') {
        setError('Security error. Ensure you are on the correct domain and try again.');
      } else {
        setError('Authentication failed. Please try again.');
        console.error('[MfaVerifyStep] WebAuthn error:', e);
      }
      setLoading(false);
    }
  }

  function retryWebAuthn() {
    webAuthnStarted.current = false;
    webAuthnStarted.current = true;
    startWebAuthn();
  }

  // ── TOTP ─────────────────────────────────────────────────────────────────

  async function handleTotpSubmit() {
    const code = totpCode.replace(/\s/g, '');
    if (!/^\d{6}$/.test(code)) {
      setError('Please enter the 6-digit code from your authenticator app.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const result = await callVerify('totp', { challengeToken, code }, functionsBaseUrl);
      if (result.error) {
        setError(result.error || 'Invalid code. Please try again.');
        setLoading(false);
        return;
      }
      onSuccess(result.token, result.user);
    } catch {
      setError('Network error. Please try again.');
      setLoading(false);
    }
  }

  // ── Shared styles ─────────────────────────────────────────────────────────

  const monoLabel = {
    display: 'block',
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: 'var(--fg3)',
    fontFamily: 'var(--font-mono)',
    marginBottom: 6,
  };

  const backBtn = {
    width: '100%',
    marginTop: 10,
    padding: '8px 0',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: 12,
    color: 'var(--fg3)',
    fontFamily: 'var(--font-mono)',
    textDecoration: 'underline',
    textDecorationColor: 'var(--border)',
  };

  // The way out for a user whose authenticator is gone, replaced, or holding a
  // dead entry. Without it this screen is a dead end that can only be escaped by
  // an administrator — which is how a self-serviceable problem becomes a ticket.
  const RecoveryLink = () =>
    onRecovery ? (
      <button type="button" style={{ ...backBtn, marginTop: 2 }} onClick={onRecovery}>
        Lost your authenticator?
      </button>
    ) : null;

  // ── WebAuthn screen ───────────────────────────────────────────────────────

  if (method === 'webauthn') {
    return (
      <>
        <div style={{ marginBottom: 20, fontSize: 14, fontWeight: 600, color: 'var(--fg)' }}>
          Verify your identity
        </div>

        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <div
            style={{
              width: 56,
              height: 56,
              margin: '0 auto 16px',
              borderRadius: '50%',
              background: loading ? 'var(--bg3)' : 'var(--accent-muted, var(--bg3))',
              border: '1px solid var(--border)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 24,
            }}
          >
            🔑
          </div>
          <div style={{ fontSize: 13, color: 'var(--fg2)', lineHeight: 1.7, marginBottom: 4 }}>
            {loading
              ? 'Waiting for your authenticator…'
              : 'Use your passkey, fingerprint, or security key to sign in.'}
          </div>
          {loading && (
            <div
              style={{
                fontSize: 11,
                color: 'var(--fg3)',
                fontFamily: 'var(--font-mono)',
                marginTop: 8,
              }}
            >
              Follow the prompt on your device
            </div>
          )}
        </div>

        {error && (
          <div
            style={{
              padding: '8px 12px',
              borderRadius: 8,
              background: 'var(--danger-bg)',
              border: '1px solid rgba(251,113,133,0.25)',
              color: 'var(--danger)',
              fontSize: 12,
              marginBottom: 14,
            }}
          >
            {error}
          </div>
        )}

        {!loading && error && (
          <button
            className="btn btn-primary"
            style={{
              width: '100%',
              justifyContent: 'center',
              padding: '11px 0',
              fontSize: 13,
              marginBottom: 6,
            }}
            onClick={retryWebAuthn}
          >
            Try again
          </button>
        )}

        <button style={backBtn} onClick={onBack}>
          ← Back to sign in
        </button>
        <RecoveryLink />
      </>
    );
  }

  // ── TOTP screen ───────────────────────────────────────────────────────────

  return (
    <>
      <div style={{ marginBottom: 20, fontSize: 14, fontWeight: 600, color: 'var(--fg)' }}>
        Enter authentication code
      </div>

      <div style={{ fontSize: 13, color: 'var(--fg2)', lineHeight: 1.5, marginBottom: 20 }}>
        {totpTag ? (
          <>
            Open your authenticator app and use the Tryggr entry ending{' '}
            <span
              style={{
                fontFamily: 'var(--font-mono)',
                color: 'var(--fg)',
                background: 'var(--bg3)',
                border: '1px solid var(--border)',
                borderRadius: 5,
                padding: '1px 6px',
              }}
            >
              {totpTag}
            </span>
            . Older Tryggr entries will not work.
          </>
        ) : (
          'Open your authenticator app and enter the 6-digit code.'
        )}
      </div>

      <div style={{ marginBottom: 20 }}>
        <label style={monoLabel}>Authentication code</label>
        <input
          className="input"
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          value={totpCode}
          onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          onKeyDown={(e) => e.key === 'Enter' && handleTotpSubmit()}
          placeholder="000000"
          style={{
            letterSpacing: '0.3em',
            textAlign: 'center',
            fontSize: 22,
            fontFamily: 'var(--font-mono)',
          }}
          autoFocus
        />
      </div>

      {error && (
        <div
          style={{
            padding: '8px 12px',
            borderRadius: 8,
            background: 'var(--danger-bg)',
            border: '1px solid rgba(251,113,133,0.25)',
            color: 'var(--danger)',
            fontSize: 12,
            marginBottom: 16,
          }}
        >
          {error}
        </div>
      )}

      <button
        className="btn btn-primary"
        style={{ width: '100%', justifyContent: 'center', padding: '11px 0', fontSize: 13 }}
        onClick={handleTotpSubmit}
        disabled={loading || totpCode.length !== 6}
      >
        {loading ? 'Verifying…' : 'Verify code'}
      </button>

      <button style={backBtn} onClick={onBack}>
        ← Back to sign in
      </button>
      <RecoveryLink />
    </>
  );
}

export default MfaVerifyStep;
